import {
  CustomToolMockResponseType,
  type CustomToolMock,
} from "@repo/app-types/entities/tools/custom";
import { formatJson } from "../codeFormatting.js";

export const toolMockResponseMaxLength = 40000;

/** A tool's simulation mock as the form edits it. */
export interface ToolMockFormSettings {
  mockResponse: string;
  mockResponseType: CustomToolMockResponseType;
  mockResponseError: string | undefined;
}

export function initializeToolMockFormSettings(
  mock: CustomToolMock | undefined,
): ToolMockFormSettings {
  return {
    mockResponse: mock?.response ?? "",
    mockResponseType: mock?.type ?? CustomToolMockResponseType.STRING,
    mockResponseError: undefined,
  };
}

type ToolMockSaveResult =
  | { isValid: true; mock: CustomToolMock | undefined }
  | { isValid: false; mockResponseError: string };

/** A JSON mock response formatted, or nothing when that changes nothing. */
export function formatToolMockFormSettings(
  settings: ToolMockFormSettings,
): Partial<ToolMockFormSettings> {
  if (settings.mockResponseType !== CustomToolMockResponseType.JSON) return {};
  const mockResponse = formatJson(settings.mockResponse);
  return mockResponse === settings.mockResponse ? {} : { mockResponse };
}

/** The mock to save, `undefined` when the response is empty. */
export function buildToolMock(
  settings: ToolMockFormSettings,
): ToolMockSaveResult {
  const response = settings.mockResponse.trim();
  const isJson = settings.mockResponseType === CustomToolMockResponseType.JSON;
  // An empty mock of either type is no mock; a non-empty JSON one must be JSON.
  if (response.length === 0) {
    return { isValid: true, mock: undefined };
  }

  // The textarea enforces this for a string; the JSON editor does not.
  if (response.length > toolMockResponseMaxLength) {
    return {
      isValid: false,
      mockResponseError: `The mock response can be at most ${toolMockResponseMaxLength.toLocaleString()} characters long.`,
    };
  }

  if (isJson) {
    try {
      JSON.parse(response);
    } catch {
      return { isValid: false, mockResponseError: "Invalid JSON format" };
    }
  }

  return {
    isValid: true,
    mock: { response, type: settings.mockResponseType },
  };
}
